import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import Shake from "shake.js";
import createRequestGuard from "./utils/requestGuard";
import { hasProgramExpired } from "./utils/programDuration";
import { isIOSLikeDevice, detectDeviceCapabilities } from "./utils/deviceCapabilities";
import "./App.css";
import "./help-modal-styles.css";
import ShakeHome from "./components/ShakeHome";
import MyEntriesPage from "./components/MyEntriesPage";
import MyRewardsPage from "./components/MyRewardsPage";
import PermissionModal from "./components/PermissionModal";
import RewardPresentation from "./components/RewardPresentation";
import SessionEndedModal from "./components/SessionEndedModal";
import Toast from "./components/Toast";
import useProgramDeadline from "./hooks/useProgramDeadline";
import useShakeFlow from "./hooks/useShakeFlow";
import useToast from "./hooks/useToast";
import useVioAuth from "./hooks/useVioAuth";
import {
  rewardsAPI,
  programAPI,
  entriesAPI,
} from "./services/api";
import { formatEntryHistoryItem } from "./utils/entryFormatters";
import { getDayKey, getStartsIn } from "./utils/programTime";
import { getListDateLocale } from "./utils/dateLocale";
import { pickUnseenRecentWin, markRewardSeen } from "./utils/recentWin";
import { sanitizeHelpHtml } from "./utils/sanitizeHtml";
import { SHAKE_TUNING, getShakeThreshold } from "./config/shakeTuning";
import {
  buildMemberVoucherUrl,
  buildVioVoucherPath,
  getVoucherPwaBaseUrl,
} from "./utils/voucherNavigation";
import { resolveVioAssetUrl } from "./utils/vioAssetUrl";
import { getParentOrigin, isEmbedded } from "./utils/parentOrigin";

// Detection tuning lives in config/shakeTuning.js.
const PRE_SHAKE_ANIMATION_DURATION = SHAKE_TUNING.preShakeAnimationMs;
const SHAKE_SAMPLE_INTERVAL_MS = SHAKE_TUNING.sampleIntervalMs;
const SHAKE_DETECTION_WINDOW_MS = SHAKE_TUNING.detectionWindowMs;
const SHAKE_EVENT_COUNT_THRESHOLD = SHAKE_TUNING.eventCountThreshold;
const SHAKE_COOLDOWN_MS = SHAKE_TUNING.cooldownMs;
const SHAKE_POST_RESULT_LOCK_MS = SHAKE_TUNING.postResultLockMs;
const SHAKE_REARM_QUIET_MS = SHAKE_TUNING.rearmQuietMs;
const SHAKE_QUIET_SPEED_FACTOR = SHAKE_TUNING.quietSpeedFactor;

// Fixed English messages that come back without a code (backend, axios, or our
// token refresh helper). Each maps to a translation; anything else gets the
// generic "failed" line.
const KNOWN_SHAKE_ERROR_MESSAGES = {
  "No active program found": "shakeErrors.noActiveProgram",
  "Internal server error": "shakeErrors.internalError",
  "Authentication required": "shakeErrors.authRequired",
  "Access token required": "shakeErrors.accessTokenRequired",
  "Invalid or expired token. Please re-authenticate.": "shakeErrors.invalidToken",
  "User not found": "shakeErrors.userNotFound",
  "Tenant context is required": "shakeErrors.tenantRequired",
  "Invalid shake payload": "shakeErrors.invalidPayload",
  "Failed to refresh access token": "shakeErrors.refreshFailed",
  "Network Error": "shakeErrors.network",
};

// `data.reason` on a shake that did not use up an entry.
const NON_CONSUMED_TOAST_BY_REASON = {
  OUT_OF_STOCK: "game.outOfStock",
  NO_REWARDS_AVAILABLE: "game.noWinnableReward",
  DELIVERY_FAILED: "toast.deliveryFailed",
  SHAKE_IN_PROGRESS: "game.previousProcessing",
};

const isRunningInIframe = () => {
  try {
    return window.self !== window.top;
  } catch (_) {
    return true;
  }
};

const App = () => {
  const { t } = useTranslation();
  const isEmbeddedFrame = isRunningInIframe();
  // Current user's reward notification (replaces modal)
  const [currentUserReward, setCurrentUserReward] = useState(null);
  const [permissionGranted, setPermissionGranted] = useState(false); // Always start as false, check permissions when needed
  const [permissionRequested, setPermissionRequested] = useState(() => {
    // Check if permission has been requested before (persisted across page refreshes)
    return localStorage.getItem('shakePermissionRequested') === 'true';
  });
  const [isShaking, setIsShaking] = useState(false);
  const [boxShakeAnimation, setBoxShakeAnimation] = useState(false); // For CSS shake animation before opening
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [permissionModalShown, setPermissionModalShown] = useState(false);
  const [showSessionEndedModal, setShowSessionEndedModal] = useState(false);
  const [showNoShakesLeftModal, setShowNoShakesLeftModal] = useState(false);
  const [presentedReward, setPresentedReward] = useState(null);
  const [showMyEntries, setShowMyEntries] = useState(false);
  const [showMyRewards, setShowMyRewards] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [userRewards, setUserRewards] = useState([]);
  const [isLoadingRewards, setIsLoadingRewards] = useState(false);
  const [rewardsLoadError, setRewardsLoadError] = useState(false);
  const [entryHistory, setEntryHistory] = useState([]);
  const [entryHistoryLoaded, setEntryHistoryLoaded] = useState(false);
  const [entryHistoryLoadError, setEntryHistoryLoadError] = useState(false);
  // Toast notification for shake limit reached
  const { showToast, toastMessage, showToastNotification } = useToast();
  const [isLoadingEntries, setIsLoadingEntries] = useState(false);
  // Session timeout management
  const [sessionActive, setSessionActive] = useState(true);
  // Who is playing: from VIO only (URL tokens / host message), see useVioAuth.
  // authProblem: null | "signin" (no usable VIO session, e.g. opened outside
  // VIO or the session expired) | "legal" (terms pending in VIO).
  const { user, isAuthenticated, authProblem, setAuthProblem, authLoading } = useVioAuth();
  const [rewardResult, setRewardResult] = useState(null);
  const [shakeCount, setShakeCount] = useState(0);
  const [programData, setProgramData] = useState(null);
  const [inactiveProgramContent, setInactiveProgramContent] = useState(null);
  // Scheduled program that hasn't started yet ("Starts in …", not "ended").
  const [upcomingProgram, setUpcomingProgram] = useState(null);
  const [nowTick, setNowTick] = useState(() => Date.now());
  const upcomingStartsIn = upcomingProgram
    ? getStartsIn(upcomingProgram.duration?.startDateTime, nowTick)
    : null;
  const formatProgramStart = (program) => {
    const start = new Date(program?.duration?.startDateTime);
    if (Number.isNaN(start.getTime())) return "";
    return start.toLocaleString(getListDateLocale(), {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: program?.timezone || undefined,
    });
  };
  // Single source of truth for shake balance (gained - used).
  // Replaces the legacy `remainingShakes` / `availableEntries` / `dailyShakeLimit`
  // / `todayShakeCount` quartet that used to drift out of sync.
  const [balance, setBalance] = useState(0);
  // Total entries count from API (all entries ever created, not just remaining)
  const [totalEntriesCount, setTotalEntriesCount] = useState(0);
  // Track if balance has been loaded from API
  const [balanceLoaded, setBalanceLoaded] = useState(false);
  // Track if limit warning has been shown in current session (resets on page refresh)
  const [limitWarningShown, setLimitWarningShown] = useState(false);
  
  // Motion can't drive the game here (no sensor, desktop, permission denied or
  // blocked inside the host iframe): offer the tap button instead.
  const [motionUnavailable, setMotionUnavailable] = useState(
    () => !detectDeviceCapabilities().supportsMotion
  );

  const shakeInstance = useRef(null);
  const deviceMotionHandler = useRef(null);
  const shakeJsEventHandler = useRef(null);
  const deviceOrientationHandler = useRef(null);
  // Refs to track page state for shake detection (avoid closure issues)
  const showMyRewardsRef = useRef(false);
  const showMyEntriesRef = useRef(false);
  // Ref to track animation key for forcing GIF reload
  const giftBoxAnimationKey = useRef(0);
  // Ref to track animation timeout to ensure minimum duration
  const animationTimeoutRef = useRef(null);
  const isPreShakeAnimating = useRef(false);
  const sessionId = useRef(
    `session_${Date.now()}_${Math.random().toString(36).substring(7)}`
  );
  const lastShakeTime = useRef(0);
  const shakeLockUntilRef = useRef(0);
  // Track shake detection - changed to immediate trigger like button
  const shakeStartTime = useRef(0); // When shake was first detected
  const lastShakeDetectionTime = useRef(0); // Last time shake was detected
  const lastShakeCompletedTime = useRef(0); // Track when shake was completed to prevent loadBalance from overriding

  // Debug logging system for mobile debugging
  const [, setDebugLogs] = useState([]);
  const maxDebugLogs = 20; // Keep only last 20 logs
  const {
    hasRewardToShow,
    getRewardDisplayName,
    buildPresentedReward,
  } = useShakeFlow();

  // Enhanced console.log that also adds to debug panel
  const debugLog = (message, data = null) => {
    const timestamp = new Date().toLocaleTimeString();
    const logEntry = {
      timestamp,
      message: data ? `${message}: ${JSON.stringify(data)}` : message
    };

    console.log(`🐛 ${message}`, data); // Also log to console with emoji prefix

    setDebugLogs(prevLogs => {
      const newLogs = [...prevLogs, logEntry];
      return newLogs.length > maxDebugLogs ? newLogs.slice(-maxDebugLogs) : newLogs;
    });
  };

  const dismissIOSUndoContext = () => {
    if (!isIOSLikeDevice()) return;
    try {
      const activeElement = document.activeElement;
      if (
        activeElement &&
        (activeElement.tagName === "INPUT" ||
          activeElement.tagName === "TEXTAREA" ||
          activeElement.isContentEditable)
      ) {
        activeElement.blur();
      }

      const selection = window.getSelection?.();
      if (selection && selection.rangeCount > 0) {
        selection.removeAllRanges();
      }
    } catch (error) {
      console.warn("⚠️ Failed to clear iOS undo context:", error);
    }
  };
  // Refs that mirror state for shake detection / async callbacks (avoid closure staleness).
  const balanceRef = useRef(0);
  const balanceRequests = useRef(createRequestGuard());
  const balanceLoadedRef = useRef(false);
  const permissionGrantedRef = useRef(false);
  const sessionActiveRef = useRef(true);
  const showSessionEndedModalRef = useRef(false);
  const dailyLoginPromiseRef = useRef(null);
  const dailyLoginDayKeyRef = useRef(null);
  const shakeInitializedRef = useRef(false);
  const sessionEndedAutoShownRef = useRef(false);
  // Track whether the user has interacted (for APIs that require gesture)
  const userInteractedRef = useRef(false);

  useEffect(() => {
    balanceLoadedRef.current = balanceLoaded;
  }, [balanceLoaded]);

  useEffect(() => {
    balanceRef.current = balance;
  }, [balance]);

  useEffect(() => {
    sessionActiveRef.current = sessionActive;
  }, [sessionActive]);

  useEffect(() => {
    showSessionEndedModalRef.current = showSessionEndedModal;
  }, [showSessionEndedModal]);

  // Apply an authoritative balance value from API responses.
  const applyBalance = (nextBalance) => {
    balanceRequests.current.invalidate();
    const safe = Math.max(0, Number.isFinite(nextBalance) ? nextBalance : 0);
    setBalance(safe);
    balanceRef.current = safe;
    setBalanceLoaded(true);
    balanceLoadedRef.current = true;
  };

  const setShakePermissionState = (granted) => {
    setPermissionGranted(granted);
    permissionGrantedRef.current = granted;
    localStorage.setItem("shakePermissionGranted", String(granted));
    if (granted) {
      setPermissionRequested(true);
      localStorage.setItem("shakePermissionRequested", "true");
    }
  };

  const holdShakeDetection = (durationMs = SHAKE_POST_RESULT_LOCK_MS) => {
    const nextUnlockTime = Date.now() + durationMs;
    shakeLockUntilRef.current = Math.max(shakeLockUntilRef.current, nextUnlockTime);
  };

  // Restore the shake permission state saved by an earlier visit.
  useEffect(() => {
    // Restore permission state from localStorage after authentication
    const savedPermission = localStorage.getItem('shakePermissionGranted');
    if (savedPermission === 'true') {
      console.log("🔄 Restoring shake permission state from localStorage");
      setPermissionGranted(true);
      permissionGrantedRef.current = true;

      // Immediately initialize shake detection since permission is already granted
      console.log("🚀 Permission restored - immediately initializing shake detection...");
      setTimeout(() => {
        window.motionEventCount = 0;
        initializeShake({ force: true });
      }, 100);

      // iOS asks for motion access again in a new page session, so a stored
      // "granted" can be stale: no devicemotion event ever arrives and shaking
      // silently does nothing. Devices with sensors fire devicemotion
      // continuously (even lying still); if none arrives, ask again instead.
      if (detectDeviceCapabilities().canRequestMotionPermission) {
        let motionSeen = false;
        const onMotion = () => { motionSeen = true; };
        window.addEventListener("devicemotion", onMotion, { once: true });
        setTimeout(() => {
          window.removeEventListener("devicemotion", onMotion);
          if (!motionSeen) {
            console.log("📱 No motion events since restore - asking for motion permission again");
            setShakePermissionState(false);
          }
        }, 2000);
      }
    }
  }, []);

  // Auto-authenticate user on app start
  // Load active program data
  const extractInactiveProgramContent = (payload) => {
    const inactiveProgram = payload?.data?.inactiveProgram || payload?.inactiveProgram;
    if (!inactiveProgram?.title && !inactiveProgram?.description) {
      return null;
    }
    return inactiveProgram;
  };

  const loadProgramData = async () => {
    try {
      console.log("🔍 Loading active program data...");
      const response = await programAPI.getActiveProgram();
      if (response.success) {
        setProgramData(response.data);
        setInactiveProgramContent(null);
        setUpcomingProgram(null);
        console.log("✅ Program data loaded:", response.data.title);

        // Extract entry configuration
        const entryConfig = response.data.entryConfig || {};
        console.log("📊 Entry configuration:", entryConfig);

        return response.data;
      }
    } catch (error) {
      console.log("⚠️ Could not load program data:", error);
      
      // Check if error is 404 (No active program found)
      // Axios errors have error.response.status and error.response.data
      const is404 = error.response?.status === 404;
      const isNoActiveProgram = error.response?.data?.message === "No active program found" || 
                                 error.response?.data?.message?.includes("No active program");
      
      console.log("🔍 Program data error details:", {
        status: error.response?.status,
        message: error.response?.data?.message,
        is404,
        isNoActiveProgram,
        errorCode: error.code
      });
      
      if (is404 || isNoActiveProgram) {
        // Not running now: either scheduled to start later ("Starts in …") or ended.
        const upcoming = error.response?.data?.data?.upcomingProgram || null;
        setProgramData(null);
        setUpcomingProgram(upcoming);
        setInactiveProgramContent(
          upcoming || extractInactiveProgramContent(error.response?.data)
        );
        setSessionActive(false);
        // Show the Session Ended modal (the upcoming variant when it starts later)
        if (!sessionEndedAutoShownRef.current) {
          setShowSessionEndedModal(true);
          sessionEndedAutoShownRef.current = true;
          console.log("📱 Session Ended modal shown - no active program");
        }
        return null;
      }
      
      // Use fallback data if API fails for other reasons. The help modal renders
      // its title and description from help.fallbackTitle / help.fallbackDescription.
      const fallbackProgram = { isFallback: true };
      setProgramData(fallbackProgram);
      return fallbackProgram;
    }

    return null;
  };

  // Calendar day in the program's time zone - the same day boundary the
  // backend uses to decide whether today's daily login bonus was granted.
  const getProgramDayKey = () => getDayKey(programData?.timezone);

  const handleDailyLogin = () => {
    if (!user || !isAuthenticated) {
      console.log("⚠️ User not authenticated, skipping daily login check");
      return Promise.resolve();
    }

    const dayKey = getProgramDayKey();

    // The cached promise is only reused for the SAME calendar day. A page left
    // open past midnight re-checks, and a failed attempt clears the cache below
    // so the next trigger can retry instead of replaying the failed result.
    if (dailyLoginPromiseRef.current && dailyLoginDayKeyRef.current === dayKey) {
      console.log("✅ Daily login check already done/in progress today, reusing promise");
      return dailyLoginPromiseRef.current;
    }

    dailyLoginDayKeyRef.current = dayKey;

    // Resolves to true only when the check reached a conclusive answer.
    const attempt = (async () => {
      try {
        const statusResponse = await rewardsAPI.getDailyLoginStatus();
        if (!statusResponse.success) {
          console.error("❌ Failed to check daily login status:", statusResponse.message);
          return false;
        }

        const { dailyLoginEnabled, hasReceivedToday, dailyLoginValue } = statusResponse.data;
        if (!dailyLoginEnabled) {
          console.log("📅 Daily login bonus is not enabled for this program");
          return true;
        }
        if (hasReceivedToday) {
          console.log("📅 Daily login bonus already received today");
          // Still refresh balance so PWA reflects DB state on resume.
          await loadBalance();
          return true;
        }

        console.log(`🎁 Granting daily login bonus: +${dailyLoginValue} entries`);
        const grantResponse = await rewardsAPI.grantDailyLogin();

        if (grantResponse.success) {
          if (typeof grantResponse.data?.balance === 'number') {
            applyBalance(grantResponse.data.balance);
          } else {
            await loadBalance();
          }
          console.log(`✅ Daily login bonus granted: +${dailyLoginValue}`);
          return true;
        }

        console.error("❌ Failed to grant daily login:", grantResponse.message);
        return false;
      } catch (error) {
        const alreadyReceived =
          error.response?.status === 400 && error.response?.data?.data?.alreadyReceived;
        if (alreadyReceived) {
          console.log("📅 Daily login already granted (concurrent request)");
          await loadBalance();
          return true;
        }
        console.error("❌ Error handling daily login:", error);
        return false;
      }
    })();

    const guarded = attempt.then((succeeded) => {
      if (!succeeded && dailyLoginPromiseRef.current === guarded) {
        // Let the next trigger (permission grant, program reload, ...) retry.
        dailyLoginPromiseRef.current = null;
        dailyLoginDayKeyRef.current = null;
      }
    });

    dailyLoginPromiseRef.current = guarded;

    return guarded;
  };

  // Keep the latest handler reachable from the long-lived visibility listener
  // below, which is registered once with an empty dependency array.
  const loadProgramDataRef = useRef(null);
  const recentWinCheckedRef = useRef(null); // userId whose recent win was checked
  useEffect(() => {
    loadProgramDataRef.current = loadProgramData;
  });
  const handleDailyLoginRef = useRef(null);
  // Same for loadBalance and the sign-in state: the listener's own closure
  // is from the first render, where loadBalance always bailed out (signed out).
  const visibilityRef = useRef({ loadBalance: null, signedIn: false });
  useEffect(() => {
    handleDailyLoginRef.current = handleDailyLogin;
    visibilityRef.current = { loadBalance, signedIn: isAuthenticated && !!user };
  });

  const helpContent = programData || inactiveProgramContent;
  // Program title/description are entered by ops; only the built-in fallback is translated.
  const helpTitle = helpContent?.isFallback
    ? t("help.fallbackTitle")
    : helpContent?.title || t("help.defaultTitle");
  const helpDescriptionHtml = helpContent?.isFallback
    ? t("help.fallbackDescription")
    : helpContent?.description || t("help.defaultHtml");

  const handleVoucherClick = (voucher) => {
    if (voucher.type === "token") return;

    // Not in VIO yet (send pending / being retried): nothing to open.
    if (voucher.deliveryStatus === "processing") {
      showToastNotification("toast.rewardProcessing", 4000);
      return;
    }

    // VIO's voucher page needs the UserVoucher id VIO returned on send. The
    // local reward id used to be the fallback and opened a wrong/empty page.
    const userVoucherId = voucher.voucherNftId || voucher.vioUserVoucherId;
    if (!userVoucherId) {
      showToastNotification("toast.voucherMissingId", 4000);
      return;
    }

    // Inside the Member App, let the host open its own voucher page: no full
    // reload of the member app, and it works without top-navigation rights.
    const navigatePath = buildVioVoucherPath(userVoucherId);
    const hostOrigin = getParentOrigin();
    if (navigatePath && isEmbedded() && hostOrigin) {
      window.parent.postMessage({ type: "VIO_NAVIGATE", payload: { path: navigatePath } }, hostOrigin);
      return;
    }

    // Same pattern as luckydraw pwa-frontend MyRewards: {redirectUrl}/voucher/{UserVoucher _id}
    const baseUrl = getVoucherPwaBaseUrl(voucher);
    const url = buildMemberVoucherUrl(baseUrl, userVoucherId);

    if (!url) {
      const hint = !baseUrl
        ? "toast.voucherMissingBaseUrl"
        : !userVoucherId
          ? "toast.voucherMissingId"
          : "toast.voucherInvalidLink";
      showToastNotification(hint, 4000);
      return;
    }

    const isInIframe = window.parent !== window;
    if (isInIframe) {
      window.top.location.href = url;
    } else {
      window.location.href = url;
    }
  };

  useEffect(() => {
    sessionEndedAutoShownRef.current = false;
    if (!programData) return;
    if (!hasProgramExpired(programData)) {
      setSessionActive(true);
      setShowSessionEndedModal(false);
    }
  }, [programData]);

  // Check if program duration has expired
  const isProgramExpired = (programInfo = programData) => hasProgramExpired(programInfo);

  const hasUserParticipatedInProgram = (programInfo, entries = entryHistory) => {
    if (
      !programInfo?.duration?.enabled ||
      !programInfo?.duration?.startDateTime ||
      !programInfo?.duration?.endDateTime
    ) {
      return true; // If we can't determine the window, don't block the user
    }

    if (!Array.isArray(entries) || entries.length === 0) {
      return false;
    }

    const startTime = new Date(programInfo.duration.startDateTime);
    const endTime = new Date(programInfo.duration.endDateTime);

    return entries.some((entry) => {
      if (!entry?.date) return false;
      const entryDate = new Date(entry.date);
      if (Number.isNaN(entryDate.getTime())) return false;

      const source = typeof entry.source === "string" ? entry.source.toLowerCase() : "";
      const description = typeof entry.description === "string" ? entry.description.toLowerCase() : "";

      const isShakeEntry =
        source === "shake" ||
        description.includes("shake");

      return isShakeEntry && entryDate >= startTime && entryDate <= endTime;
    });
  };

  // Check program expiration on page load and show Session Ended modal if needed
  const checkProgramExpiredOnLoad = (
    programInfo = programData,
    {
      entryHistoryData,
      entryHistoryReady = entryHistoryLoaded && !entryHistoryLoadError,
    } = {}
  ) => {
    if (!programInfo) return false;

    if (!entryHistoryReady) {
      console.log("⏳ Entry history not ready - deferring Session Ended check");
      return false;
    }

    if (!isProgramExpired(programInfo)) {
      console.log("✅ Program is still active");
      return false;
    }

    const entriesToCheck =
      entryHistoryData !== undefined ? entryHistoryData : entryHistory;

    const userParticipated = hasUserParticipatedInProgram(
      programInfo,
      entriesToCheck
    );

    setSessionActive(false);

    if (userParticipated) {
      console.log(
        "✅ Program duration ended but user already participated - skipping Session Ended notification"
      );
      return false;
    }

    console.log(
      "🎯 Program duration has expired and user missed the session - showing Session Ended modal"
    );
    setSessionActive(false);

    if (!sessionEndedAutoShownRef.current) {
      setShowSessionEndedModal(true);
      sessionEndedAutoShownRef.current = true;
    }

    return true;
  };

  // Load user rewards from API
  const loadUserRewards = async () => {
    try {
      if (!isAuthenticated || !user) {
        console.log("⚠️ User not authenticated, skipping rewards loading");
        return;
      }

      console.log("🔍 Loading user rewards...");
      setIsLoadingRewards(true);
      setRewardsLoadError(false);

      const response = await rewardsAPI.getUserRewards();
      if (response.success) {
        const normalizedRewards = (response.data.rewards || []).map((reward) => ({
          ...reward,
          voucherImage: resolveVioAssetUrl(reward.voucherImage),
          image: reward.image?.imageUrl
            ? {
                ...reward.image,
                imageUrl: resolveVioAssetUrl(reward.image.imageUrl),
              }
            : reward.image,
          company: reward.company
            ? {
                ...reward.company,
                logo: reward.company.logo?.imageUrl
                  ? {
                      ...reward.company.logo,
                      imageUrl: resolveVioAssetUrl(reward.company.logo.imageUrl),
                    }
                  : reward.company.logo,
              }
            : reward.company,
        }));
        setUserRewards(normalizedRewards);

        // The host reloads the iframe on token rotation: a prize won just
        // before must still be presented after the reload.
        if (recentWinCheckedRef.current !== user.userId) {
          recentWinCheckedRef.current = user.userId;
          const win = pickUnseenRecentWin(normalizedRewards, user.userId);
          if (win && !isProcessingShake.current && !isPreShakeAnimating.current) {
            // List rows carry the prize name as source / voucherName.
            const named = { ...win, name: win.name || win.voucherName || win.source };
            const shown = buildPresentedReward(named, getRewardDisplayName(named));
            markRewardSeen(user.userId, shown.id);
            setPresentedReward(shown);
            if (!shown.pending) setCurrentUserReward(shown);
          }
        }
        console.log(
          "✅ User rewards loaded:",
          normalizedRewards.length,
          "rewards"
        );
        
        // Log voucher details including voucherNftId for debugging
        const vouchers = normalizedRewards.filter(r => r.type === 'voucher');
        if (vouchers.length > 0) {
          console.log('📋 Voucher details:');
          vouchers.forEach((v, index) => {
            console.log(`  ${index + 1}. ${v.source || 'Unknown'}:`);
            console.log(`     - ID: ${v.id}`);
            console.log(`     - VoucherNftId: ${v.voucherNftId || '❌ MISSING'}`);
            console.log(`     - Expiry: ${v.expiryEndDatetime || 'No expiry'}`);
          });
        }
      } else {
        console.error("❌ Failed to load user rewards:", response.message);
        setRewardsLoadError(true);
      }
    } catch (error) {
      console.error("❌ Error loading user rewards:", error);
      setRewardsLoadError(true);
    } finally {
      setIsLoadingRewards(false);
    }
  };

  // Ignore reads superseded by a newer request or a shake balance update.
  const loadBalance = async () => {
    const isCurrent = balanceRequests.current.begin();
    const isStale = () => !isCurrent() || isProcessingShake.current || isPreShakeAnimating.current;
    try {
      if (!isAuthenticated || !user) {
        return;
      }
      if (isProcessingShake.current || isPreShakeAnimating.current) {
        console.log("🔒 Skipping loadBalance - shake is in progress");
        return;
      }

      const response = await rewardsAPI.getBalance();
      if (isStale()) return;
      if (!response.success) {
        console.error("❌ Failed to load balance:", response.message);
        setBalanceLoaded(true);
        balanceLoadedRef.current = true;
        return;
      }

      const next = Math.max(0, Number(response.data?.balance) || 0);
      console.log(`✅ Balance loaded: ${next}`);
      applyBalance(next);

      if (
        permissionGrantedRef.current ||
        localStorage.getItem("shakePermissionGranted") === "true"
      ) {
        setTimeout(() => {
          window.motionEventCount = 0;
          initializeShake({ force: true });
        }, 100);
      }

      if (next > 0 && limitWarningShown) {
        setLimitWarningShown(false);
        sessionStorage.removeItem(`shake_limit_warning_${new Date().toDateString()}`);
      }
    } catch (error) {
      if (isStale()) return;
      const is404 = error.response?.status === 404;
      const isNoActiveProgram =
        error.response?.data?.message?.includes?.("No active program");
      if (is404 || isNoActiveProgram) {
        console.log("🎯 No active program - showing session ended");
        setProgramData(null);
        setInactiveProgramContent(extractInactiveProgramContent(error.response?.data));
        setSessionActive(false);
        if (!sessionEndedAutoShownRef.current) {
          setShowSessionEndedModal(true);
          sessionEndedAutoShownRef.current = true;
        }
        setBalanceLoaded(true);
        balanceLoadedRef.current = true;
        return;
      }
      console.error("❌ Error loading balance:", error);
      setBalanceLoaded(true);
      balanceLoadedRef.current = true;
    }
  };

  const loadEntryHistory = useCallback(
    async ({ silent = false } = {}) => {
      if (!isAuthenticated || !user) {
        setEntryHistory([]);
        setEntryHistoryLoaded(false);
        return [];
      }

      if (!silent) {
        setIsLoadingEntries(true);
      }
      setEntryHistoryLoaded(false);
      setEntryHistoryLoadError(false);

      try {
        const params = {
          page: 1,
          limit: 100,
          sortBy: "createdAt",
          sortOrder: "desc",
        };
        const response = await entriesAPI.getUserEntries(params);

        if (response.success) {
          const history =
            response.data?.entries?.map(formatEntryHistoryItem) || [];
          setEntryHistory(history);
          
          // CRITICAL: Use stats.overall.totalEntries for balance (entries gained - entries used)
          // This represents the user's current available entries, NOT the count of Entry records
          // stats.overall.totalEntries = entriesGained - entriesUsed (e.g., 10 from daily login - 1 shake = 9)
          const totalEntriesBalance = response.data?.stats?.overall?.totalEntries;
          const entriesGained = response.data?.stats?.overall?.entriesGained;
          const entriesUsed = response.data?.stats?.overall?.entriesUsed;
          
          // Use totalEntriesBalance (the actual balance), not pagination.total (count of records)
          const totalEntries = totalEntriesBalance ?? 0;
          
          setTotalEntriesCount(totalEntries);
          setEntryHistoryLoaded(true);
          setEntryHistoryLoadError(false);
          
          console.log("📊 Entry history loaded from API:", {
            entriesReturned: history.length,
            totalEntriesBalance,
            entriesGained,
            entriesUsed,
            formula: `${entriesGained} - ${entriesUsed} = ${totalEntriesBalance}`,
            totalEntriesUsed: totalEntries,
            pagination: response.data?.pagination,
            stats: response.data?.stats
          });
          
          return history;
        } else {
          console.error(
            "❌ Failed to load entry history from API:",
            response.message
          );
          setEntryHistory([]);
          setEntryHistoryLoaded(true);
          setEntryHistoryLoadError(true);
          return [];
        }
      } catch (error) {
        console.error("❌ Error loading entry history from API:", error);
        setEntryHistory([]);
        setEntryHistoryLoaded(true);
        setEntryHistoryLoadError(true);
        return [];
      } finally {
        if (!silent) {
          setIsLoadingEntries(false);
        }
      }
    },
    [isAuthenticated, user]
  );


  useEffect(() => {
    // The first program load happens in initializeApp once the member is
    // signed in: loading here, before the URL tokens were exchanged, sent no
    // token (or a previous session's) and raced the sign-in.

    // Refresh program data + balance when the page becomes visible again.
    // The legacy 30s polling loop has been removed - it caused 7 -> 5 -> 7
    // jitter when failed/out-of-stock shakes left the optimistic state ahead
    // of what the server reported. The server is now the single authority
    // for `balance` and we only re-fetch on visible focus.
    const onVisibility = () => {
      if (document.visibilityState !== "visible") return;
      if (isProcessingShake.current || isPreShakeAnimating.current) return;
      if (!visibilityRef.current.signedIn) return;
      // Re-check the daily login bonus: a PWA left open past midnight (program
      // time zone) has a new bonus waiting, and a previously failed check retries.
      handleDailyLoginRef.current?.();
      loadProgramData().then(() => {
        if (!isProcessingShake.current && !isPreShakeAnimating.current) {
          visibilityRef.current.loadBalance?.();
        }
      });
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  // Upcoming program: keep "Starts in …" current and switch to the live
  // program once it starts (no reload needed).
  useEffect(() => {
    const start = new Date(upcomingProgram?.duration?.startDateTime).getTime();
    if (!Number.isFinite(start)) return undefined;
    const tick = () => {
      setNowTick(Date.now());
      if (Date.now() >= start) loadProgramDataRef.current?.();
    };
    const interval = setInterval(tick, 30 * 1000);
    const startTimer = setTimeout(tick, Math.min(Math.max(start - Date.now() + 1000, 0), 2147483647));
    return () => {
      clearInterval(interval);
      clearTimeout(startTimer);
    };
  }, [upcomingProgram]);

  // Ref to track if we're currently processing a shake
  const isProcessingShake = useRef(false);

  // Reset shake processing states on component mount
  useEffect(() => {
    console.log("🏁 Component mounted, resetting shake states");
    isProcessingShake.current = false;
    isPreShakeAnimating.current = false;
    // CRITICAL FIX: Ensure shake initialization flag is reset on mount
    // This prevents stale state from previous sessions causing shake detection to fail
    shakeInitializedRef.current = false;
    console.log("✅ Shake initialization flag reset on mount - ensures fresh initialization");
  }, []);

  // Check user entry status when user and program data are loaded
  useEffect(() => {
    console.log("🔍 useEffect triggered, checking conditions:", {
      hasUser: !!user,
      hasProgramData: !!programData,
      isProcessingShake: isProcessingShake.current,
      isPreShakeAnimating: isPreShakeAnimating.current,
      allConditionsMet: !!(user && programData && !isProcessingShake.current && !isPreShakeAnimating.current)
    });

    if (user && programData && !isProcessingShake.current && !isPreShakeAnimating.current) {
      console.log("🔄 useEffect: loading entry history, daily login, rewards, balance");
      loadEntryHistory()
        .then((history) => {
          checkProgramExpiredOnLoad(programData, {
            entryHistoryData: history,
            entryHistoryReady: true,
          });
        })
        .catch((error) => {
          console.error("❌ Error during entry history load:", error);
        });

      handleDailyLogin();
      loadUserRewards();

      setTimeout(() => {
        if (!isProcessingShake.current && !isPreShakeAnimating.current) {
          loadBalance();
        }
      }, 500);
    }
  }, [user, programData]);

  // Sync refs with state for shake detection (ensure refs are always up-to-date)
  useEffect(() => {
    showMyRewardsRef.current = showMyRewards;
    showMyEntriesRef.current = showMyEntries;
    console.log(`📱 Page state updated: MyRewards=${showMyRewards}, MyEntries=${showMyEntries}, Shake detection enabled on main page: ${!showMyRewards && !showMyEntries}`);
    
    // Ensure body classes are synced with state (cleanup on component unmount or state change)
    if (showMyEntries) {
      document.body.classList.add('my-entries-active');
      document.body.classList.remove('my-rewards-active');
    } else if (showMyRewards) {
      document.body.classList.add('my-rewards-active');
      document.body.classList.remove('my-entries-active');
    } else {
      document.body.classList.remove('my-entries-active', 'my-rewards-active');
    }
  }, [showMyRewards, showMyEntries]);

  useEffect(() => {
    if (showMyEntries && isAuthenticated && user) {
      loadEntryHistory({ silent: false });
    }
  }, [showMyEntries, isAuthenticated, user, loadEntryHistory]);

  useEffect(() => {
    if (
      !isAuthenticated ||
      !user ||
      !programData ||
      !entryHistoryLoaded ||
      entryHistoryLoadError
    ) {
      return;
    }

    checkProgramExpiredOnLoad(programData, { entryHistoryReady: true });
  }, [
    isAuthenticated,
    user,
    programData,
    entryHistoryLoaded,
    entryHistoryLoadError,
    entryHistory,
  ]);

  // Cleanup: Remove body classes on component unmount
  useEffect(() => {
    return () => {
      document.body.classList.remove('my-entries-active', 'my-rewards-active');
    };
  }, []);

  const triggerPreShakeAnimation = (onComplete, options = {}) => {
    const {
      vibratePattern = [200, 100, 200],
      source = "unknown",
    } = options;

    if (isProcessingShake.current || isPreShakeAnimating.current) {
      console.log(
        `🔁 Pre-shake animation skipped (${source}) - shake already in progress`
      );
      return;
    }

    const giftSection = document.querySelector(".gift-section");
    isPreShakeAnimating.current = true;

    if (giftSection) {
      giftSection.classList.add("shaking");
      console.log(`🎁 Pre-shake animation started (${source})`);
    } else {
      console.warn(
        `⚠️ Gift section not found when starting pre-shake animation (${source})`
      );
    }

    // Use unified haptic feedback (supports iOS fallback)
    if (vibratePattern) {
      triggerHapticFeedback(vibratePattern);
    }

    setTimeout(() => {
      if (giftSection) {
        giftSection.classList.remove("shaking");
      }
      isPreShakeAnimating.current = false;
      console.log(
        `🎁 Pre-shake animation completed (${source}) - proceeding to reward flow`
      );
      if (typeof onComplete === "function") {
        onComplete();
      }
    }, PRE_SHAKE_ANIMATION_DURATION);
  };

  // Enhanced shake handling with gift box opening animation
  // FOLLOW EXACT INSTRUCTION: 1 shake = 1 complete sequence (present shake → lid open + confetti → reward)
  const handleShake = async () => {
    // iOS Safari may show "Shake to Undo" when editable context remains active.
    dismissIOSUndoContext();

    // CRITICAL: Prevent concurrent shake processing - ONE shake = ONE complete reward sequence
    if (isProcessingShake.current) {
      console.log("🔄 Already processing a shake - ONE shake = ONE complete sequence, ignoring additional shakes...");
      return;
    }
    
    if (!isAuthenticated || !sessionActiveRef.current || isProgramExpired()) {
      showToastNotification("toast.sessionUnavailable");
      return;
    }

    // Set processing flag IMMEDIATELY to prevent multiple shakes from triggering
    isProcessingShake.current = true;
    balanceRequests.current.invalidate();
    holdShakeDetection(SHAKE_COOLDOWN_MS);
    
    const currentTime = Date.now();
    lastShakeTime.current = currentTime;
    
    console.log("🎯 STEP 1: Shake phone detected! Starting complete sequence:");

    shakeStartTime.current = 0;
    lastShakeDetectionTime.current = 0;

    triggerHapticFeedback([50, 30, 100, 30, 50]);

    // Capture authoritative pre-shake balance for rollback on failure / no-consume.
    const prevBalance = balanceRef.current;
    debugLog(`📊 Balance BEFORE shake: ${prevBalance} (loaded=${balanceLoadedRef.current})`);

    if (balanceLoadedRef.current && prevBalance <= 0) {
      console.log("❌ No balance left - showing limit-reached UI");
      setRewardResult({
        data: { entry: { rewardGranted: false }, reward: null, noRewardsAvailable: false },
        message: "You've used all your shake entries.",
        success: false,
      });
      setIsShaking(true);
      setTimeout(() => {
        setIsShaking(false);
        isProcessingShake.current = false;
        holdShakeDetection(SHAKE_POST_RESULT_LOCK_MS);
      }, 1500);
      return;
    }

    // Optimistic decrement so the digit drops immediately on shake.
    if (prevBalance > 0) {
      const optimistic = prevBalance - 1;
      setBalance(optimistic);
      balanceRef.current = optimistic;
      debugLog(`✅ Optimistic balance ${prevBalance} -> ${optimistic}`);
    }

    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
      animationTimeoutRef.current = null;
    }
    setPresentedReward(null);

    setBoxShakeAnimation(true);
    setTimeout(async () => {
      setBoxShakeAnimation(false);
      if (!isShaking) {
        giftBoxAnimationKey.current += 1;
        setIsShaking(true);
      }

      const animationStartTime = Date.now();
      const minAnimationDuration = 1500;
      animationTimeoutRef.current = setTimeout(() => {}, minAnimationDuration);

      if (!isAuthenticated) {
        console.warn("⚠️ Shake blocked because user is not authenticated");
        showToastNotification("toast.restartFromOps");
        setIsShaking(false);
        isProcessingShake.current = false;
        isPreShakeAnimating.current = false;
        holdShakeDetection(SHAKE_POST_RESULT_LOCK_MS);
        return;
      }

      const rollbackBalance = () => {
        setBalance(prevBalance);
        balanceRef.current = prevBalance;
      };

      try {
        if (!sessionId.current || sessionId.current.trim() === '') {
          sessionId.current = `session_${Date.now()}_${Math.random().toString(36).substring(7)}`;
        }
        const shakeData = {
          shakeCount: 1,
          sessionId: sessionId.current,
          deviceData: {},
        };

        const response = await rewardsAPI.processShake(shakeData);
        debugLog("📥 Shake API response:", response?.data);

        // Server is the single source of truth - apply its balance unconditionally.
        if (typeof response?.data?.balance === 'number') {
          applyBalance(response.data.balance);
        }

        const consumed = response?.data?.consumed === true;
        const reason = response?.data?.reason || null;
        setRewardResult(response);

        if (!consumed) {
          if (typeof response?.data?.balance !== 'number') rollbackBalance();
          // Out of stock / no rewards configured / probability miss - balance NOT consumed.
          // applyBalance() above has already restored to server-truth (== prevBalance).
          console.log(`⚠️ Shake not consumed (${reason || 'no reward'}) - balance preserved`);
          showToastNotification(
            NON_CONSUMED_TOAST_BY_REASON[reason] ||
              KNOWN_SHAKE_ERROR_MESSAGES[response?.message] ||
              "toast.shakeNotCounted",
            4000
          );
          setIsShaking(false);
          isProcessingShake.current = false;
          holdShakeDetection(SHAKE_POST_RESULT_LOCK_MS);
          if (isAuthenticated && user) {
            loadEntryHistory({ silent: true });
          }
          return;
        }

        const elapsed = Date.now() - animationStartTime;
        const remainingTime = Math.max(minAnimationDuration - elapsed, 0);

        setTimeout(async () => {
          const shouldShowReward = hasRewardToShow(response);
          if (!shouldShowReward) {
            setIsShaking(false);
            isProcessingShake.current = false;
            holdShakeDetection(SHAKE_POST_RESULT_LOCK_MS);
            return;
          }

          const reward = response.data.reward;
          const rewardName = getRewardDisplayName(reward);
          const displayReward = buildPresentedReward(reward, rewardName);
          setPresentedReward(displayReward);
          // The "You shook and won …" banner only for prizes VIO confirmed.
          if (!displayReward.pending) setCurrentUserReward(displayReward);
          markRewardSeen(user?.userId, displayReward.id);

          if (isAuthenticated && user) {
            loadUserRewards();
            loadEntryHistory({ silent: true });
          }
          lastShakeCompletedTime.current = Date.now();
          holdShakeDetection(SHAKE_POST_RESULT_LOCK_MS);

          if (balanceRef.current === 0 && isProgramExpired()) {
            setTimeout(() => setShowSessionEndedModal(true), 3000);
          }
        }, remainingTime);
      } catch (error) {
        console.error("❌ Error processing shake:", error);
        isProcessingShake.current = false;
        setIsShaking(false);
        setBoxShakeAnimation(false);
        isPreShakeAnimating.current = false;
        holdShakeDetection(SHAKE_POST_RESULT_LOCK_MS);

        // Server returns balance even in error responses; prefer that, otherwise rollback.
        const errBalance = error.response?.data?.data?.balance;
        if (typeof errBalance === 'number') {
          applyBalance(errBalance);
        } else {
          rollbackBalance();
        }

        const code = error.response?.data?.errorCode;
        if (code === 'INSUFFICIENT_BALANCE' || error.response?.data?.data?.noShakesLeft) {
          setShowNoShakesLeftModal(true);
          return;
        }

        // No answer (timeout / connection dropped): the server may still grant
        // this shake. Don't call it failed — say it's still processing and
        // re-read balance, rewards and entries once the server is done.
        if (!error.response && (error.code === 'ECONNABORTED' || error.message === 'Network Error')) {
          showToastNotification("toast.shakeInProgress", 5000);
          setTimeout(() => {
            if (isProcessingShake.current) return;
            loadBalance();
            loadUserRewards();
            loadEntryHistory({ silent: true });
          }, 5000);
          return;
        }

        const message =
          error.response?.data?.message ||
          error.message ||
          "Failed to process shake. Please try again.";
        showToastNotification(KNOWN_SHAKE_ERROR_MESSAGES[message] || "toast.shakeFailed", 4000);
        setRewardResult({
          success: false,
          message,
          data: {
            entry: { rewardGranted: false },
            reward: null,
            noRewardsAvailable: error.response?.data?.data?.noRewardsAvailable || false,
          },
        });
      }
    }, 150);
  };

  // No more Session Ended modal - just let the user continue without interruption
  

  // Helper function to detect if app is running as PWA
  const isPWA = () => {
    // Check for standalone display mode (Android/Desktop PWA)
    if (window.matchMedia('(display-mode: standalone)').matches) return true;
    // Check for standalone mode (iOS PWA)
    if (window.navigator.standalone) return true;
    // Check for fullscreen mode
    if (window.matchMedia('(display-mode: fullscreen)').matches) return true;
    return false;
  };

  // Enhanced shake detection with cross-device support
  const isEmbeddedInIframe = () => {
    try {
      return window.self !== window.top;
    } catch (e) {
      // Cross-origin access throws, which itself means we are in an iframe
      return true;
    }
  };

  // Motion can't be used here. Never navigate the host window away (that used
  // to replace the whole VIO Member App with this page, refreshToken in the URL
  // and no way back): explain, and fall back to the tap button.
  const notifyShakeBlocked = ({ reason, inIframe, isIOSChrome }) => {
    console.warn("⚠️ Motion permission blocked:", { reason, inIframe, isIOSChrome });
    setMotionUnavailable(true);
    let message;
    if (inIframe) {
      message = "toast.motionBlockedIframe";
    } else if (isIOSChrome) {
      // iOS Chrome / Firefox / Edge often silently deny motion even at top level due to WKWebView limits.
      message = "toast.motionOpenSafari";
    } else {
      message = "toast.motionDenied";
    }
    showToastNotification(message, 6000);
  };

  const requestShakePermissionFromGesture = async () => {
    const deviceInfo = detectDeviceCapabilities();
    const inIframe = isEmbeddedInIframe();
    const isIOSChrome = !!(deviceInfo.isIOSChrome || deviceInfo.isIOSFirefox || deviceInfo.isIOSEdge);
    const canRequestMotionPermission =
      typeof window.DeviceMotionEvent !== "undefined" &&
      typeof window.DeviceMotionEvent.requestPermission === "function";

    if (canRequestMotionPermission) {
      console.log("📱 Browser requires explicit motion permission - requesting now...", {
        isIOS: deviceInfo.isIOS,
        isIOSChrome,
        inIframe,
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        maxTouchPoints: navigator.maxTouchPoints,
        isSecureContext: window.isSecureContext,
      });
      try {
        const permission = await window.DeviceMotionEvent.requestPermission();
        console.log("📱 Motion permission result:", permission);

        if (permission === "granted") {
          setShakePermissionState(true);
          setMotionUnavailable(false);
          return true;
        }

        console.warn("⚠️ Motion permission not granted:", permission);
        setShakePermissionState(false);
        notifyShakeBlocked({ reason: `permission=${permission}`, inIframe, isIOSChrome });
        return false;
      } catch (permissionError) {
        console.error("❌ Error requesting motion permission:", permissionError);
        setShakePermissionState(false);
        notifyShakeBlocked({
          reason: permissionError?.message || "request rejected",
          inIframe,
          isIOSChrome,
        });
        return false;
      }
    }

    // Browsers without an explicit prompt (most Android) still need Permissions Policy
    // delegation when embedded. Probe once to confirm devicemotion events flow.
    console.log("📱 Browser does not expose motion permission prompt - treating motion as permissionless", {
      isIOS: deviceInfo.isIOS,
      inIframe,
      hasDeviceMotion: deviceInfo.hasDeviceMotion,
    });

    if (inIframe && deviceInfo.hasDeviceMotion) {
      const motionAvailable = await new Promise((resolve) => {
        let received = false;
        const probe = () => {
          received = true;
          window.removeEventListener("devicemotion", probe, false);
          resolve(true);
        };
        try {
          window.addEventListener("devicemotion", probe, { once: true });
        } catch (e) {
          resolve(false);
          return;
        }
        setTimeout(() => {
          if (!received) {
            window.removeEventListener("devicemotion", probe, false);
            resolve(false);
          }
        }, 1500);
      });

      if (!motionAvailable) {
        console.warn("⚠️ No devicemotion events received inside iframe within 1.5s - likely blocked by Permissions Policy");
        setShakePermissionState(false);
        notifyShakeBlocked({ reason: "no devicemotion events", inIframe: true, isIOSChrome });
        return false;
      }
    }

    setShakePermissionState(true);
    setMotionUnavailable(!deviceInfo.supportsMotion);
    return true;
  };

  // Enhanced shake detection with multiple fallback methods
  const initializeShake = async ({ force = false } = {}) => {
    // CRITICAL: Allow initialization even if shakesDataLoaded is false
    // Shake detection will check for valid data when shake happens
    // This ensures shake detection is ready immediately

    // Prevent duplicate initialization
    if (shakeInitializedRef.current && !force) {
      debugLog("🔄 Shake detection already initialized, skipping...");
      return;
    }

    try {
      const deviceInfo = detectDeviceCapabilities();
      
      // CRITICAL FIX: Clean up any existing listeners before adding new ones
      // This prevents duplicate event listeners if initializeShake is called multiple times
      // Only clean up if we're actually re-initializing (shakeInitializedRef is true)
      if (shakeInitializedRef.current || force) {
        debugLog("🧹 Re-initializing shake detection - cleaning up existing listeners");

        if (deviceMotionHandler.current) {
          try {
            window.removeEventListener("devicemotion", deviceMotionHandler.current, false);
            deviceMotionHandler.current = null; // Clear the reference
            debugLog("🧹 Removed existing devicemotion listener");
          } catch (e) {
            // Ignore errors if listener doesn't exist
          }
        }

        if (deviceOrientationHandler.current) {
          try {
            window.removeEventListener("deviceorientation", deviceOrientationHandler.current, false);
            deviceOrientationHandler.current = null;
            debugLog("🧹 Removed existing deviceorientation listener");
          } catch (e) {
            // Ignore errors if listener doesn't exist
          }
        }

        if (shakeJsEventHandler.current) {
          try {
            window.removeEventListener("shake", shakeJsEventHandler.current, false);
            shakeJsEventHandler.current = null;
            debugLog("🧹 Removed existing Shake.js event listener");
          } catch (e) {
            // Ignore errors if listener doesn't exist
          }
        }

        if (shakeInstance.current) {
          try {
            shakeInstance.current.stop();
            shakeInstance.current = null;
            debugLog("🧹 Cleaned up existing Shake.js instance");
          } catch (e) {
            // Ignore errors
          }
        }
      }

      let shakeDetectionMethods = 0;
      
        // Method 1: Custom DeviceMotionEvent implementation
      if (deviceInfo.hasDeviceMotion) {
        
        let lastX = null, lastY = null, lastZ = null, lastTime = 0;
        const shakeThreshold = getShakeThreshold(deviceInfo);
        const quietSpeed = shakeThreshold * SHAKE_QUIET_SPEED_FACTOR;
        let shakeEvents = [];
        let shakeArmed = true;
        let quietSince = Date.now();
        const rememberAcceleration = (acceleration, time) => {
          lastX = acceleration.x || 0;
          lastY = acceleration.y || 0;
          lastZ = acceleration.z || 0;
          lastTime = time;
        };
        
        deviceMotionHandler.current = (event) => {
          // DEBUG: Log that device motion event is being received (only first few times to avoid spam)
          if (!window.motionEventCount) window.motionEventCount = 0;
          window.motionEventCount++;
          // if (window.motionEventCount <= 10) { // 增加到10次以获得更多信息
          //   debugLog("📱 DeviceMotionEvent received:", {
          //     eventCount: window.motionEventCount,
          //     shakesDataLoaded,
          //     permissionGranted: permissionGrantedRef.current,
          //     showMyRewards: showMyRewardsRef.current,
          //     showMyEntries: showMyEntriesRef.current,
          //     isMobile: deviceInfo.isMobile,
          //     isTablet: deviceInfo.isTablet,
          //     hasAcceleration: !!(event.acceleration || event.accelerationIncludingGravity),
          //     acceleration: event.accelerationIncludingGravity ? {
          //       x: event.accelerationIncludingGravity.x?.toFixed(2),
          //       y: event.accelerationIncludingGravity.y?.toFixed(2),
          //       z: event.accelerationIncludingGravity.z?.toFixed(2)
          //     } : null
          //   });
          // }

          // CRITICAL: Only process shake on main page, NOT on My Rewards or My Entries pages
          // No logging here: this runs ~60 times a second, and debugLog sets
          // state, which re-rendered the whole app while My Rewards / My
          // Entries was open.
          if (showMyRewardsRef.current || showMyEntriesRef.current) {
            return;
          }

          // CRITICAL: Check if we have valid shake data before allowing shake
          // Allow shake if: 
          // 1) data is loaded from API, OR 
          // 2) we have remainingShakes value, OR
          // 3) localStorage has todayShakes data (can calculate remainingShakes)
          // This ensures shake works immediately even if API data hasn't loaded yet
          if (!balanceLoadedRef.current) {
            const now = Date.now();
            if (!window.lastDataNotLoadedLog || now - window.lastDataNotLoadedLog > 5000) {
              debugLog(`⏳ Skipping shake - balance not loaded yet`);
              window.lastDataNotLoadedLog = now;
            }
            return;
          }

          // ENHANCED: 确保只在移动设备上工作，PC端完全不触发
          if (!deviceInfo.isMobile && !deviceInfo.isTablet) {
            return;
          }


          const currentTime = Date.now();
          const timeDifference = currentTime - lastTime;

          if (timeDifference >= SHAKE_SAMPLE_INTERVAL_MS) {
            // Try multiple acceleration sources for compatibility
            const acceleration = event.accelerationIncludingGravity || event.acceleration;
            
            if (!acceleration || acceleration.x === null || acceleration.y === null || acceleration.z === null) {
              return;
            }

            if (lastX === null || lastY === null || lastZ === null) {
              lastX = acceleration.x || 0;
              lastY = acceleration.y || 0;
              lastZ = acceleration.z || 0;
              lastTime = currentTime;
              return;
            }
            
              const deltaX = Math.abs(acceleration.x - lastX);
              const deltaY = Math.abs(acceleration.y - lastY);
              const deltaZ = Math.abs(acceleration.z - lastZ);

              const speed = ((deltaX + deltaY + deltaZ) / timeDifference) * 1000;
              const now = Date.now();

              if (speed <= quietSpeed) {
                if (!quietSince) {
                  quietSince = now;
                }

                if (
                  !shakeArmed &&
                  now >= shakeLockUntilRef.current &&
                  now - quietSince >= SHAKE_REARM_QUIET_MS
                ) {
                  shakeArmed = true;
                  shakeEvents = [];
                  debugLog("✅ Shake detector re-armed after quiet period");
                }
              } else {
                quietSince = 0;
              }

              if (!shakeArmed || now < shakeLockUntilRef.current) {
                lastX = acceleration.x || 0;
                lastY = acceleration.y || 0;
                lastZ = acceleration.z || 0;
                lastTime = currentTime;
                return;
              }

              if (isProcessingShake.current || isPreShakeAnimating.current) {
                lastX = acceleration.x || 0;
                lastY = acceleration.y || 0;
                lastZ = acceleration.z || 0;
                lastTime = currentTime;
                return;
              }

              // Check if shake intensity is above threshold - requires REAL shake motion
              if (speed > shakeThreshold) {
                // 清除超过时间窗口的旧事件
                shakeEvents = shakeEvents.filter(time => now - time < SHAKE_DETECTION_WINDOW_MS);
                
                // 记录当前的剧烈运动
                shakeEvents.push(now);
                
                // debugLog(`🔔 Vigorous motion detected! Speed: ${speed.toFixed(2)} | Shake events in window: ${shakeEvents.length}/${SHAKE_COUNT_THRESHOLD}`);

                // 检查是否在时间窗口内累积了足够的摇动次数
                if (shakeEvents.length >= SHAKE_EVENT_COUNT_THRESHOLD) {
                  // debugLog(`✅ CONTINUOUS SHAKING DETECTED! ${shakeEvents.length} shakes in ${SHAKE_DETECTION_WINDOW_MS}ms window`);

                  // 检查冷却期
                  const timeSinceLastShake = now - lastShakeDetectionTime.current;
                  if (timeSinceLastShake < SHAKE_COOLDOWN_MS) {
                    // debugLog(`⏱️ Shake in cooldown period: ${timeSinceLastShake}ms < ${SHAKE_COOLDOWN_MS}ms - ignoring`);
                    rememberAcceleration(acceleration, currentTime);
                    return;
                  }

                  // Lock until the phone has both cooled down and gone quiet.
                  shakeEvents = [];
                  shakeArmed = false;
                  quietSince = 0;
                  lastShakeDetectionTime.current = now;
                  holdShakeDetection(SHAKE_COOLDOWN_MS);

              // Trigger when real shake is detected (same as pressing "Shake Now" button)
              // CRITICAL: Shaking phone has the SAME logic as pressing "Shake Now" button
              // Both call handleShake() which: 1) Decrements remainingShakes → 2) Present shakes → 3) Lid opens + confetti → 4) Shows reward
              debugLog(`✅ STEP 1: SHAKE DETECTED! Intensity: ${speed.toFixed(2)} (threshold: ${shakeThreshold})`);
              // debugLog(`📱 Continuous shaking detected (${shakeEvents.length} events in ${SHAKE_DETECTION_WINDOW}ms) - triggering reward`);
              // debugLog(`📱 Shaking phone detected - using SAME logic as "Shake Now" button`);

              if (isProcessingShake.current) {
                debugLog("🔄 Already processing a shake, ignoring...");
                rememberAcceleration(acceleration, currentTime);
                return;
              }

              if (!sessionActiveRef.current) {
                console.log("❌ Shake blocked - session has ended");
                if (!showSessionEndedModalRef.current) setShowSessionEndedModal(true);
                triggerHapticFeedback([100, 50, 100]);
                rememberAcceleration(acceleration, currentTime);
                return;
              }

              // Use refs (state can be stale inside this long-lived listener closure).
              if (balanceLoadedRef.current && balanceRef.current <= 0) {
                console.log("❌ Shake blocked - no balance left");
                setShowNoShakesLeftModal(true);
                triggerHapticFeedback([100, 50, 100]);
                rememberAcceleration(acceleration, currentTime);
                return;
              }

              // Trigger success haptics only after all blocking checks pass.
              debugLog("🔴 Attempting to trigger haptic feedback...");
              const hapticOk = triggerHapticFeedback(60);
              debugLog("📳 Haptic feedback result:", hapticOk);
              setTimeout(() => { try { triggerHapticFeedback(30); } catch (_) {} }, 500);


              handleShake();
                } else {
                  // 未达到所需的摇动次数，继续等待
                  console.log(`⏱️ Waiting for more shakes... ${shakeEvents.length}/${SHAKE_EVENT_COUNT_THRESHOLD} in window`);
                }
            }
            // Note: No need to reset tracking here since we use debouncing instead of duration tracking

              rememberAcceleration(acceleration, currentTime);
          }
        };

        try {
          window.addEventListener("devicemotion", deviceMotionHandler.current, { passive: true });
          shakeDetectionMethods++;
        } catch (motionError) {
          // Silent fail - will try other methods
        }
      }

      // Method 2: Shake.js library fallback for browsers where direct motion setup fails.
      const ShakeClass = Shake || window.Shake;
      if (ShakeClass && shakeDetectionMethods === 0) {
        console.log("📚 Setting up Shake.js library as fallback...");
        try {
          shakeInstance.current = new ShakeClass({
            threshold: deviceInfo.isIOS
              ? SHAKE_TUNING.shakeJsThreshold.ios
              : SHAKE_TUNING.shakeJsThreshold.other,
            timeout: SHAKE_COOLDOWN_MS,
          });
          shakeInstance.current.start();

          shakeJsEventHandler.current = () => {
            const now = Date.now();
            if (now < shakeLockUntilRef.current) {
              return;
            }

            // CRITICAL: Check page state first
            if (showMyRewardsRef.current || showMyEntriesRef.current) {
              console.log(`🔇 Shake detection disabled - currently on ${showMyRewardsRef.current ? 'My Rewards' : 'My Entries'} page`);
              return;
            }

            // Note: Permission check removed - shake detection will work automatically

            if (!balanceLoadedRef.current) {
              console.log('⏳ Shake detection blocked - balance still loading');
              return;
            }

            if (isProcessingShake.current || isPreShakeAnimating.current) {
              console.log("🔄 Already processing a shake - ONE shake = ONE complete sequence, ignoring...");
              return;
            }

            if (!sessionActiveRef.current) {
              console.log("❌ Shake.js shake blocked - session has ended");
              if (!showSessionEndedModalRef.current) setShowSessionEndedModal(true);
              triggerHapticFeedback([100, 50, 100]);
              holdShakeDetection(SHAKE_POST_RESULT_LOCK_MS);
              return;
            }

            if (balanceLoadedRef.current && balanceRef.current <= 0) {
              console.log("❌ Shake.js shake blocked - no balance left");
              setShowNoShakesLeftModal(true);
              triggerHapticFeedback([100, 50, 100]);
              holdShakeDetection(SHAKE_POST_RESULT_LOCK_MS);
              return;
            }

            lastShakeDetectionTime.current = now;
            holdShakeDetection(SHAKE_COOLDOWN_MS);
            console.log(`✅ STEP 1: SHAKE DETECTED (Shake.js)! Starting EXACT sequence`);
            
            // Trigger vibration feedback (supports iOS fallback)
            triggerHapticFeedback([200, 100, 200]);
            console.log("📳 Haptic feedback triggered for shake detection");
            
            // Call handleShake() - it will execute the EXACT sequence:
            // STEP 1: Shake phone ✅ (detected here)
            // STEP 2: Present moves and shakes ➡️
            // STEP 3: Present lid opens and confetti comes out ➡️
            // STEP 4: Reward shows (voucher or token) ➡️
            handleShake();
          };

          window.addEventListener("shake", shakeJsEventHandler.current, false);
          shakeDetectionMethods++;
          console.log("✅ Shake.js library initialized (fallback mode)");
        } catch (shakeError) {
          console.warn("⚠️ Shake.js setup failed:", shakeError);
        }
      }

      // Method 3: DeviceOrientationEvent as backup for some devices
      if (deviceInfo.hasDeviceOrientation && shakeDetectionMethods === 0) {
        console.log("🧭 Setting up DeviceOrientationEvent as backup...");
        let lastRotationX = 0, lastRotationY = 0, lastRotationZ = 0;
        
        deviceOrientationHandler.current = (event) => {
          const deltaX = Math.abs((event.beta || 0) - lastRotationX);
          const deltaY = Math.abs((event.gamma || 0) - lastRotationY);
          const deltaZ = Math.abs((event.alpha || 0) - lastRotationZ);
          
          const totalDelta = deltaX + deltaY + deltaZ;
          
          // Proper threshold for orientation-based shake detection - requires real shake
          if (totalDelta > SHAKE_TUNING.orientationThresholdDeg) {
            // Trigger when real shake is detected
            const now = Date.now();
            const timeSinceLastShake = now - lastShakeDetectionTime.current;
            
            // Debounce to prevent multiple rapid triggers
            if (timeSinceLastShake > SHAKE_COOLDOWN_MS && now >= shakeLockUntilRef.current) {
              lastShakeDetectionTime.current = now;
              holdShakeDetection(SHAKE_COOLDOWN_MS);
              console.log(`✅ Shake detected (DeviceOrientation)! Starting gift box shake animation...`);

              if (showMyRewardsRef.current || showMyEntriesRef.current) {
                console.log("🔇 DeviceOrientation shake ignored - not on main page");
              } else if (isProcessingShake.current || isPreShakeAnimating.current) {
                console.log("🔄 DeviceOrientation shake ignored - already processing");
              } else if (!sessionActiveRef.current) {
                console.log("❌ DeviceOrientation shake blocked - session has ended");
                if (!showSessionEndedModalRef.current) setShowSessionEndedModal(true);
                triggerHapticFeedback([100, 50, 100]);
              } else if (!balanceLoadedRef.current) {
                console.log("⏳ DeviceOrientation shake blocked - balance still loading");
              } else if (balanceLoadedRef.current && balanceRef.current <= 0) {
                console.log(`🚫 No balance left (DeviceOrientation) - showing limit UI`);
                triggerHapticFeedback([100, 50, 100]);
                setShowNoShakesLeftModal(true);
              } else {
                triggerPreShakeAnimation(() => {
                  handleShake();
                }, {
                  vibratePattern: [200, 100, 200],
                  source: "device-orientation",
                });
              }

            } else {
              console.log(`🔇 Shake debounced (${timeSinceLastShake}ms since last shake, need ${SHAKE_COOLDOWN_MS}ms)`);
            }
          }
          // Note: No need to reset tracking here since we use debouncing instead of duration tracking
          
          lastRotationX = event.beta || 0;
          lastRotationY = event.gamma || 0;
          lastRotationZ = event.alpha || 0;
        };
        
        try {
          window.addEventListener("deviceorientation", deviceOrientationHandler.current, { passive: true });
          shakeDetectionMethods++;
          console.log("✅ DeviceOrientationEvent backup added");
        } catch (orientationError) {
          console.warn("⚠️ DeviceOrientationEvent setup failed:", orientationError);
        }
      }

      debugLog(`🎉 Shake detection initialized with ${shakeDetectionMethods} method(s)`);
      debugLog("📱 You can now shake your device!");

      // Mark shake detection as initialized
      shakeInitializedRef.current = true;

      if (shakeDetectionMethods === 0) {
        debugLog("⚠️ No motion detection methods available, button-only mode enabled");
      }
      
    } catch (error) {
      console.error("❌ Failed to initialize shake detection:", error);
    }
  };

  // Handle permission modal
  const handleAllowPermission = async () => {
    console.log("✅ User clicked Allow - enabling shake detection");
    setShowPermissionModal(false);
    // Preflight haptic on user gesture to satisfy potential gesture requirements
    try {
      userInteractedRef.current = true;
      const primed = triggerHapticFeedback(30);
      console.log("🟢 Priming haptic on Allow click:", primed);
    } catch (_) {}

    const permissionReady = await requestShakePermissionFromGesture();
    if (!permissionReady) {
      return;
    }

    let activeProgram = programData;

    if (!activeProgram) {
      console.log("⏳ Program data not loaded yet - fetching before enabling permission");
      activeProgram = await loadProgramData();
    }

    if (activeProgram) {
      const historyForCheck = entryHistoryLoaded
        ? entryHistory
        : await loadEntryHistory({ silent: true });
      checkProgramExpiredOnLoad(activeProgram, {
        entryHistoryData: historyForCheck,
        entryHistoryReady: true,
      });
    }

    // Check if program duration has expired before starting session
    if (isProgramExpired(activeProgram)) {
      console.log("❌ Program has expired - evaluating participation before showing session ended");
      const historyForCheck = entryHistoryLoaded
        ? entryHistory
        : await loadEntryHistory({ silent: true });
      const userParticipated = hasUserParticipatedInProgram(
        activeProgram,
        historyForCheck
      );

      setSessionActive(false);

      if (!userParticipated && !sessionEndedAutoShownRef.current) {
        setShowSessionEndedModal(true);
        sessionEndedAutoShownRef.current = true;
      } else {
        console.log(
          userParticipated
            ? "✅ Program expired but user already participated - no modal shown"
            : "⚠️ Session Ended modal already shown once"
        );
      }
      return;
    }
    
    console.log("🎁 User granted shake permission - checking daily login + balance");
    await handleDailyLogin();
    await loadBalance();

    if (balanceRef.current <= 0) {
      console.log("❌ User has no balance - showing No Shakes Left modal");
      setShowNoShakesLeftModal(true);
      return;
    }

    startSession(activeProgram);
    await initializeShake({ force: true });
  };

  const handleDenyPermission = () => {
    console.log("⚠️ User clicked Don't Allow");
    setShowPermissionModal(false);
    // Don't save to localStorage - we want to show modal on every refresh if permission not granted
    // User can still enable manually later via the button
    setShakePermissionState(false);
  };

  const handleManualShakeRequest = async (source = "shake-button") => {
    console.log(`🖱️ Manual shake requested (${source})`);

    if (!sessionActive) {
      if (!showSessionEndedModal) setShowSessionEndedModal(true);
      showToastNotification("toast.sessionEndedRestart");
      return;
    }

    if (!balanceLoadedRef.current) {
      showToastNotification("toast.loadingShakeData");
      return;
    }

    if (isProcessingShake.current || isPreShakeAnimating.current) {
      console.log("🔁 Manual shake blocked - another shake is in progress");
      return;
    }

    if (balanceRef.current <= 0) {
      console.log("🚫 Manual shake at zero balance - refreshing from API before blocking");
      await loadBalance();

      if (balanceRef.current <= 0) {
        if (!limitWarningShown) {
          setLimitWarningShown(true);
          const today = new Date().toDateString();
          sessionStorage.setItem("lastLimitWarningDate", today);
          sessionStorage.setItem(`shake_limit_warning_${today}`, "shown");
        }
        showToastNotification("main.allUsed");
        triggerHapticFeedback([120, 80, 120]);
        return;
      }
    }


    triggerPreShakeAnimation(
      () => {
        console.log(`🎯 Pre-shake animation finished (${source}) - calling handleShake()`);
        handleShake();
      },
      {
        vibratePattern: [200, 100, 200],
        source,
      }
    );
  };

  // Manual shake for testing
  const manualShake = () => {
    handleManualShakeRequest("developer-manual");
  };

  // Navigation handlers
  const handleShowMyEntries = () => {
    setShowMyEntries(true);
    showMyEntriesRef.current = true; // Update ref for shake detection
    // Prevent body scroll when My Entries page is active
    document.body.classList.add('my-entries-active');
    document.body.classList.remove('my-rewards-active');
  };

  const handleShowMyRewards = () => {
    setShowMyRewards(true);
    showMyRewardsRef.current = true; // Update ref for shake detection
    loadUserRewards(); // Load latest rewards when opening the page
    // Prevent body scroll when My Rewards page is active
    document.body.classList.add('my-rewards-active');
    document.body.classList.remove('my-entries-active');
  };

  const handleBackToMain = () => {
    setShowMyEntries(false);
    setShowMyRewards(false);
    showMyEntriesRef.current = false; // Update ref for shake detection
    showMyRewardsRef.current = false; // Update ref for shake detection
    // Remove body scroll prevention classes
    document.body.classList.remove('my-entries-active', 'my-rewards-active');
  };

  // Auto-initialize shake detection when app loads
  useEffect(() => {
    const initializeApp = async () => {
      console.log("🚀 Initializing PWA app...");
      
      try {
        if (isAuthenticated && user) {
          console.log("📊 Loading program data...");
          await loadProgramData();
          await loadBalance();
        }
      } catch (error) {
        console.error("❌ Error during data loading:", error);
      }
      
      const deviceInfo = detectDeviceCapabilities();
      console.log("📱 Device capabilities:", deviceInfo);
      
      if (deviceInfo.supportsMotion) {
        console.log("✅ Motion-capable device detected:", {
          mobile: deviceInfo.isMobile,
          tablet: deviceInfo.isTablet,
          hasMotion: deviceInfo.hasDeviceMotion,
          platform: deviceInfo.isIOS ? 'iOS' : deviceInfo.isAndroid ? 'Android' : 'Other',
          isIOS: deviceInfo.isIOS
        });
        
        // Check if device supports motion (hasDeviceMotion or supportsMotion)
        const hasMotionSupport = deviceInfo.supportsMotion || deviceInfo.hasDeviceMotion;

        // Check if running in PWA mode (standalone)
        const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                      (window.navigator.standalone === true) ||
                      document.referrer.includes('android-app://');
        
        // Detect if this is a page refresh or initial navigation
        const navigationEntry = performance.getEntriesByType('navigation')[0];
        const isPageRefresh = navigationEntry && navigationEntry.type === 'reload';
        
        // Only show permission modal when entering PWA (not on refresh)
        if (isPWA && !isPageRefresh) {
          console.log("📱 PWA mode detected - showing shake detection permission modal on entry");
          setShowPermissionModal(true);
        } else if (!isPWA) {
          console.log("🌐 Browser mode - not showing permission modal");
        } else {
          console.log("🔄 PWA refresh detected - not showing permission modal");
        }
        
        // Don't auto-enable shake detection - wait for user permission
        // Shake detection will be initialized after user clicks "Allow"
      } else {
        // Check if running in PWA mode (standalone)
        const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                      (window.navigator.standalone === true) ||
                      document.referrer.includes('android-app://');
        
        // Detect if this is a page refresh or initial navigation
        const navigationEntry = performance.getEntriesByType('navigation')[0];
        const isPageRefresh = navigationEntry && navigationEntry.type === 'reload';
        
        // Only show permission modal when entering PWA (not on refresh)
        if (isPWA && !isPageRefresh) {
          console.log("📱 PWA mode detected (no motion support) - showing permission modal for manual shake button");
          setShowPermissionModal(true);
        } else if (!isPWA) {
          console.log("🌐 Browser mode - not showing permission modal");
        } else {
          console.log("🔄 PWA refresh detected - not showing permission modal");
        }
      }
      
      // Always log available features for debugging
      console.log("🔍 Available features:", {
        DeviceMotionEvent: 'DeviceMotionEvent' in window,
        DeviceOrientationEvent: 'DeviceOrientationEvent' in window,
        TouchSupport: deviceInfo.hasTouchSupport,
        UserAgent: navigator.userAgent
      });
    };

    initializeApp();
  }, [isAuthenticated, user]); // Add dependencies so it re-runs when auth state changes

  const startSession = useProgramDeadline(programData, setSessionActive, setShowSessionEndedModal);

  // Enhanced cleanup on unmount
  useEffect(() => {
    return () => {
      console.log("🧹 Cleaning up shake detection...");
      
      // Clean up device motion listener
      if (deviceMotionHandler.current) {
        try {
          window.removeEventListener("devicemotion", deviceMotionHandler.current, false);
          deviceMotionHandler.current = null; // Clear the reference
          console.log("✅ DeviceMotionEvent listener removed");
        } catch (error) {
          console.error("❌ Error removing DeviceMotionEvent listener:", error);
        }
      }

      if (deviceOrientationHandler.current) {
        try {
          window.removeEventListener("deviceorientation", deviceOrientationHandler.current, false);
          deviceOrientationHandler.current = null;
          console.log("✅ DeviceOrientationEvent listener removed");
        } catch (error) {
          console.error("❌ Error removing DeviceOrientationEvent listener:", error);
        }
      }

      if (shakeJsEventHandler.current) {
        try {
          window.removeEventListener("shake", shakeJsEventHandler.current, false);
          shakeJsEventHandler.current = null;
          console.log("✅ Shake.js event listener removed");
        } catch (error) {
          console.error("❌ Error removing Shake.js event listener:", error);
        }
      }

      // Clean up Shake.js instance
      if (shakeInstance.current) {
        try {
          shakeInstance.current.stop();
          shakeInstance.current = null; // Clear the reference
          console.log("✅ Shake.js instance cleaned up");
        } catch (error) {
          console.error("❌ Error cleaning up Shake.js:", error);
        }
      }

      // CRITICAL FIX: Reset shake initialization flag to allow re-initialization on page refresh
      // This ensures shake detection can be properly initialized again after cleanup
      shakeInitializedRef.current = false;
      console.log("✅ Shake initialization flag reset");

      console.log("🏁 Shake detection cleanup completed");
    };
  }, []);

  const shouldDisableShakeButton =
    isProcessingShake.current || isPreShakeAnimating.current;

  const manualShakeButtonLabel = isProcessingShake.current
    ? t("main.shakeButton.processing")
    : t("main.shakeButton.shakeNow");

  const manualShakeButtonText = (() => {
    if (!permissionGranted && !motionUnavailable) {
      return t("main.shakeButton.enableShake");
    }
    if (!balanceLoaded) {
      return t("main.shakeButton.preparing");
    }
    if (!sessionActive) {
      return upcomingProgram && !programData
        ? t("main.shakeButton.comingSoon")
        : t("main.shakeButton.sessionEnded");
    }
    if (balance <= 0) {
      return t("main.shakeButton.noShakesLeft");
    }
    return manualShakeButtonLabel;
  })();

  // Unified haptic feedback: prefer Vibration API, fallback to short WebAudio beep
  const triggerHapticFeedback = useCallback((pattern) => {
    try {
      if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
        const result = navigator.vibrate(pattern ?? 50);
        return !!result;
      }
    } catch (_) {}
    // Fallback: short audio click (useful on iOS Safari where vibrate is unsupported)
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return false;
      const ctx = new AudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'square';
      osc.frequency.setValueAtTime(180, ctx.currentTime);
      osc.connect(gain);
      gain.connect(ctx.destination);
      // short envelope (~100ms)
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + 0.01);
      // Close the context afterwards: browsers cap live AudioContexts (a new
      // one per shake used to pile up until creation failed).
      osc.onended = () => {
        try { ctx.close?.()?.catch?.(() => {}); } catch (_) {}
      };
      osc.start();
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.09);
      osc.stop(ctx.currentTime + 0.1);
      return true;
    } catch (_) {
      return false;
    }
  }, []);

  // Mark user interaction early to satisfy potential gesture requirements
  useEffect(() => {
    const markInteracted = () => { userInteractedRef.current = true; };
    window.addEventListener('touchstart', markInteracted, { passive: true });
    window.addEventListener('click', markInteracted, false);
    return () => {
      window.removeEventListener('touchstart', markInteracted);
      window.removeEventListener('click', markInteracted);
    };
  }, []);

  const activityEnded = Boolean(
    (programData && isProgramExpired(programData)) ||
    (inactiveProgramContent &&
      (inactiveProgramContent.status === "completed" || isProgramExpired(inactiveProgramContent)))
  );

  return (
    <div className={`App ${isEmbeddedFrame ? "is-embedded-frame" : ""}`}>
      {showMyRewards ? (
        /* My Rewards Page */
        <MyRewardsPage
          handleBackToMain={handleBackToMain}
          userRewards={userRewards}
          isLoadingRewards={isLoadingRewards}
          rewardsLoadError={rewardsLoadError}
          loadUserRewards={loadUserRewards}
          handleVoucherClick={handleVoucherClick}
        />
      ) : showMyEntries ? (
        /* My Entries Page */
        <MyEntriesPage
          handleBackToMain={handleBackToMain}
          totalEntriesCount={totalEntriesCount}
          isLoadingEntries={isLoadingEntries}
          entryHistory={entryHistory}
          entryHistoryLoadError={entryHistoryLoadError}
          loadEntryHistory={loadEntryHistory}
        />
      ) : (
        /* Main Page */
        <div className="container">
          <ShakeHome
            authLoading={authLoading} programData={programData} upcomingStartsIn={upcomingStartsIn}
            sessionActive={sessionActive} activityEnded={activityEnded} balance={balance} balanceLoaded={balanceLoaded}
            permissionGranted={permissionGranted} permissionRequested={permissionRequested}
            motionUnavailable={motionUnavailable} isShaking={isShaking} boxShakeAnimation={boxShakeAnimation}
            rewardResult={rewardResult} currentUserReward={currentUserReward}
            manualShakeButtonText={manualShakeButtonText} shouldDisableShakeButton={shouldDisableShakeButton}
            onAllow={handleAllowPermission} onManualShake={handleManualShakeRequest}
            onRewards={handleShowMyRewards} onEntries={handleShowMyEntries} onHelp={() => setShowHelpModal(true)}
            rewardPresentation={<RewardPresentation reward={presentedReward} onClose={() => {
              setIsShaking(false);
              setPresentedReward(null);
              isProcessingShake.current = false;
              holdShakeDetection(SHAKE_POST_RESULT_LOCK_MS);
            }} />}
          />

          <SessionEndedModal
            isOpen={showSessionEndedModal && !authProblem}
            {...(upcomingProgram && !programData
              ? {
                  title: t("upcoming.title"),
                  message: t("upcoming.message", {
                    date: formatProgramStart(upcomingProgram),
                  }),
                  subMessage: t("upcoming.subMessage"),
                  buttonText: t("common.gotIt"),
                }
              : !programData && !activityEnded
                ? { title: t("game.noEventTitle"), message: t("game.noEvent"), subMessage: "", buttonText: t("common.gotIt") }
                : {})}
            onClose={() => setShowSessionEndedModal(false)}
          />

          <SessionEndedModal
            isOpen={!!authProblem && (authProblem === "legal" || !isAuthenticated)}
            title={t(authProblem === "legal" ? "authProblem.legalTitle" : "authProblem.signinTitle")}
            message={t(authProblem === "legal" ? "authProblem.legalMessage" : "authProblem.signinMessage")}
            subMessage=""
            buttonText={t("common.gotIt")}
            onClose={() => setAuthProblem(null)}
          />

          <SessionEndedModal
            isOpen={showNoShakesLeftModal}
            title={t("noShakesModal.title")}
            message={t("noShakesModal.message")}
            subMessage={t("noShakesModal.subMessage")}
            buttonText={t("common.gotIt")}
            onClose={() => setShowNoShakesLeftModal(false)}
          />

          <PermissionModal
            isOpen={showPermissionModal}
            onAllow={handleAllowPermission}
            onDeny={handleDenyPermission}
          />

          {/* Help Modal */}
          {showHelpModal && (
            <div 
              className="permission-modal-overlay" 
              onClick={() => setShowHelpModal(false)}
              style={{ zIndex: 3000 }}
            >
              <div
                className="help-modal-content"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="help-modal-header">
                  <h2 className="help-modal-title">
                    {helpTitle}
                  </h2>
                </div>
                
                <div className="help-modal-body">
                  <div 
                    className="help-description rich-text-content"
                    dangerouslySetInnerHTML={{ __html: sanitizeHelpHtml(helpDescriptionHtml) }}
                  />
                </div>
                
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                  <button 
                    className="modal-done-btn" 
                    onClick={() => setShowHelpModal(false)}
                    style={{ 
                      marginTop: '20px',
                      padding: '12px 24px',
                      fontSize: '14px',
                      width: 'auto',
                      minWidth: '100px'
                    }}
                  >
                    {t("help.gotIt")}
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>
      )}

      <Toast isOpen={showToast} message={toastMessage} />


    </div>
  );
};

export default App;
// Force deployment - Wed Oct  8 10:31:31 HKT 2025
